// SAM-730 — the public event registration steps live in the browser history: each step forward is a
// history entry, so the browser's Back (and the in-page arrow) returns to the previous step instead
// of leaving to /events. The step is kept in `location.state`; what the visitor typed lives in the
// page's React state, so after a reload (state survives, data does not) the page starts at the detail.
import { useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export type EventStep = 'detail' | 'code' | 'phone' | 'pin' | 'companions' | 'guests' | 'success';

export const useEventStepHistory = (): { step: EventStep; goTo: (next: EventStep) => void; goBack: () => void } => {
  const location = useLocation();
  const navigate = useNavigate();
  const visited = useRef<Set<EventStep>>(new Set(['detail']));

  const fromState = (location.state as { eventStep?: EventStep } | null)?.eventStep;
  const step: EventStep = fromState && visited.current.has(fromState) ? fromState : 'detail';

  const goTo = useCallback((next: EventStep) => {
    visited.current.add(next);
    navigate({ pathname: location.pathname, search: location.search }, { state: { eventStep: next } });
  }, [navigate, location.pathname, location.search]);

  const goBack = useCallback(() => navigate(-1), [navigate]);

  return { step, goTo, goBack };
};
